import { type Movie, type SearchResponse, api, ProviderNameNetflix, ProviderNameAmazonPrime } from './shared.js'
import { elem } from './browser-shared.js'
import { type RenderMoviesOptions, renderMovies } from './render-movies.js'
import { initHideAndWatchList, loadHidelistIds, loadWatchlistIds } from "./hide-and-watch-lists.js";
import { countries } from './countries.js';

type UserChoice = { Name: string; hasPassword: boolean };
type Genre = { id: number; name: string };

const loginDialog = elem<HTMLDialogElement>("loginDialog");
const loginForm = elem<HTMLFormElement>("loginForm");
const loginUser = elem<HTMLSelectElement>("loginUser");
const loginPassword = elem<HTMLInputElement>("loginPassword");
const loginRepeat = elem<HTMLInputElement>("loginRepeat");
const repeatRow = elem<HTMLElement>("repeatRow");
const loginError = elem<HTMLElement>("loginError");
const userDisplay = elem<HTMLInputElement>("userDisplay");
const genre = elem<HTMLSelectElement>("genre");
const searchButton = elem<HTMLButtonElement>("searchButton");
const status = elem<HTMLElement>("status");
const filmList = elem<HTMLElement>("filmList");
const filmListEmpty = elem<HTMLElement>("filmListEmpty");
const appMain = elem<HTMLElement>("appMain");
const aboutPage = elem<HTMLElement>("aboutPage");
const creditsPage = elem<HTMLElement>("creditsPage");
const impressumPage = elem<HTMLElement>("impressumPage");
const privacyPage = elem<HTMLElement>("privacyPage");
const menuContainer = elem<HTMLElement>("menuContainer");
const menuButton = elem<HTMLButtonElement>("menuButton");
const mainMenu = elem<HTMLElement>("mainMenu");
const passwordResetButton = elem<HTMLButtonElement>("passwordResetButton");
const logoutMenuButton = elem<HTMLButtonElement>("logoutMenuButton");
const serviceSelect = elem<HTMLSelectElement>('service');
const releaseYearFrom = elem<HTMLInputElement>('releaseYearFrom');
const releaseYearTo = elem<HTMLInputElement>('releaseYearTo');

const services = [
  'Alle',
  ProviderNameAmazonPrime,
  ProviderNameNetflix
];

const pages: Record<string, HTMLElement> = {
  about: aboutPage,
  credits: creditsPage,
  impressum: impressumPage,
  privacy: privacyPage
};

let users: UserChoice[] = [];

const filmRows = new Map<number, HTMLElement>();

(async function start(){
  try {
    debugger; // Set your breakpoints now!

    window.addEventListener('hashchange', handlePageNavigation);
    handlePageNavigation();

    setServices();
    setCountries();
    initReleaseYeareFields();
    await loadUsers();
    const session=await api<{authenticated:boolean;user:string}>('/api/login');
    if (session.authenticated && session.user) { 
      userDisplay.value=session.user; 
      menuContainer.classList.remove('hidden'); 
      await loadGenres(); 
      await loadHidelistIds(); 
      await loadWatchlistIds(); 
    }
    else loginDialog.showModal();
  } catch(e) { loginError.textContent=(e as Error).message; if (!loginDialog.open) loginDialog.showModal(); }
})();

function setServices() {
  serviceSelect.innerHTML = '';
  services.forEach((service) => {
    const option = document.createElement('option');
    option.value = service;
    option.textContent = service;

    if (service === 'Alle') {
      option.selected = true;
    }

    serviceSelect.appendChild(option);
  });
}

function setCountries() {
  const select = elem<HTMLSelectElement>('countries');

  select.replaceChildren();

  for (const country of countries) {
    const option = document.createElement('option');

    option.value = country.iso_3166_1;
    option.textContent = country.native_name;
    if (country.native_name === "Alle") {
      option.selected = true;
    }

    select.appendChild(option);
  }
}

function initReleaseYeareFields() {
  const currentYear = new Date().getFullYear();

  const releaseYearFrom = elem<HTMLInputElement>('releaseYearFrom');
  const releaseYearTo = elem<HTMLInputElement>('releaseYearTo');

  releaseYearFrom.min = '1900';
  releaseYearFrom.max = String(currentYear);

  releaseYearTo.min = '1900';
  releaseYearTo.max = String(currentYear);
}

async function search() {
  const currentYear = new Date().getFullYear();
  let relYearFrom = 0;
  let relYearTo = 0;
  if (isInteger(releaseYearFrom.value)) {
    relYearFrom = +releaseYearFrom.value
  }
  if (isInteger(releaseYearTo.value)) {
    relYearTo = +releaseYearTo.value
  }
  if (relYearFrom !== 0 && (relYearFrom < 1900 || relYearFrom > currentYear)) {
    releaseYearFrom.focus();
    status.textContent = `Ungültiges Jahr bei "Kinostart >=". Erlaubt sind 1900–${currentYear}.`;
    return;
  }
  if (relYearTo !== 0 && (relYearTo < 1900 || relYearTo > currentYear)) {
    releaseYearTo.focus();
    status.textContent = `Ungültiges Jahr bei "Kinostart <=". Erlaubt sind 1900–${currentYear}.`;
    return;
  }

  filmRows.clear();
  filmList.replaceChildren();
  
  searchButton.disabled = true;
  status.textContent = 'Suche läuft …';

  try {
    const ratingValue = Math.max(
      0,
      Number(elem<HTMLInputElement>('rating').value || 0)
    );

    const maxTime = Math.max(
      0,
      Math.floor(Number(elem<HTMLInputElement>('maxTime').value || 0))
    );

    const allResults: Movie[] = [];

    let page = 1;
    let hasMore = true;
    let totalPages = 0;
    let elapsedMs = 0;

    const deadline =
      maxTime > 0
      ? performance.now() + maxTime * 1000
      : Number.POSITIVE_INFINITY;

    while (hasMore && performance.now() < deadline) {
      const data = await api<SearchResponse>(
        '/api/films',
        {
          method: 'POST',
          body: JSON.stringify({
            service: serviceSelect.value,
            rating: ratingValue,
            genre: Number(genre.value),
            releaseYearFrom: relYearFrom !== 0 ? `${relYearFrom}-01-01` : "",
            releaseYearTo: relYearTo !== 0 ? `${relYearTo}-12-31` : "",
            countries: getSelectedCountries(),
            text: elem<HTMLInputElement>('textSearch').value,
            sort: elem<HTMLSelectElement>('sort').value,
            showHiddenFilms:
              elem<HTMLSelectElement>('showHiddenFilms').value,
            maxTime,
            page
          })
        }
      );

      allResults.push(...data.results);

      totalPages = data.totalPages;
      elapsedMs += data.elapsedMs;

      const renderMovieOptions: RenderMoviesOptions = {
        filmRows,
        movies: allResults,
        filmlistFont: data.filmlistFont,
        previewFont: data.previewFont
      };
      renderMovies(renderMovieOptions);

      status.textContent =
        `${allResults.length} Filme · Seite ${page - 1}/${totalPages} · ${elapsedMs} ms`;

      hasMore = data.hasMore;
      page++;
    }

    status.textContent =
      `${allResults.length} Filme · ${elapsedMs} ms · ${totalPages} Seiten`;

  } catch (e) {
    status.textContent = (e as Error).message;
    filmList.replaceChildren();
    filmListEmpty.classList.remove('hidden');
    filmListEmpty.textContent = 'Fehler bei der Suche.';
  } finally {
    searchButton.disabled = false;
  }
}

function getSelectedCountries(): string[] {
  const select = elem<HTMLSelectElement>('countries');

  return Array.from(select.selectedOptions)
    .map((option) => option.value);
}

function showPage(pageName: string) {
  // Hauptansicht und alle Unterseiten ausblenden
  appMain.classList.add('hidden');
  
  Object.values(pages).forEach((page) => {
    page.classList.add('hidden');
  });
  
  // Startseite anzeigen
  if (!pageName || pageName === 'home') { 
    appMain.classList.remove('hidden');
    return;
  }
  
  // Gewünschte Seite anzeigen
  const page = pages[pageName];
  if (page) {
    page.classList.remove('hidden');
  } else {
    appMain.classList.remove('hidden');
  }
}

function handlePageNavigation() { 
  const pageName = window.location.hash.substring(1);
  showPage(pageName || 'home'); 
}

async function loadUsers() {
  const data = await api<{Users:UserChoice[]}>('/api/users');
  users = data.Users;
  loginUser.replaceChildren(...users.map((u) => new Option(u.Name, u.Name)));
  updatePasswordMode();
}

function updatePasswordMode() {
  const user = users.find((u) => u.Name === loginUser.value);
  repeatRow.classList.toggle('hidden', Boolean(user?.hasPassword));
  loginRepeat.required = !user?.hasPassword;
  loginPassword.autocomplete = user?.hasPassword ? 'current-password' : 'new-password';
  loginError.textContent = '';
}

async function login() {
  const user = users.find((u) => u.Name === loginUser.value);
  if (!user) return;
  const data = await api<{ok:boolean;user:string}>('/api/login', { method:'POST', body:JSON.stringify({ user:user.Name, password:loginPassword.value, repeat:loginRepeat.value }) });
  userDisplay.value = data.user;
  loginPassword.value = ''; loginRepeat.value = '';
  loginDialog.close();
  menuContainer.classList.remove('hidden');
  await loadGenres();
  await loadHidelistIds();
  await loadWatchlistIds();
}

async function loadGenres() {
  const data = await api<{genres:Genre[]}>('/api/genres');
  genre.replaceChildren(...data.genres.map((g) => new Option(g.name, String(g.id))));
}

function isInteger(value: string): boolean {
  return value.trim() !== '' && Number.isInteger(Number(value));
}
  
loginUser.addEventListener('change',updatePasswordMode);
loginForm.addEventListener('submit',(event)=>{ event.preventDefault(); login().catch((e)=>loginError.textContent=(e as Error).message); });
searchButton.addEventListener('click',()=>void search());

function closeMenu() {
  mainMenu.classList.add("hidden");
  menuButton.setAttribute("aria-expanded", "false");
}

function toggleMenu() {
  const isOpen = !mainMenu.classList.contains("hidden");

  if (isOpen) {
    closeMenu();
  } else {
    mainMenu.classList.remove("hidden");
    menuButton.setAttribute("aria-expanded", "true");
  }
}

document.querySelectorAll('#mainMenu a.menu-item').forEach((item) => {
  item.addEventListener('click', () => {
    closeMenu();
  });
});

menuButton.addEventListener("click", (event) => {
  event.stopPropagation();
  toggleMenu();
});

document.addEventListener("click", (event) => {
  if (!menuContainer.contains(event.target as Node)) {
    closeMenu();
  }
});

logoutMenuButton.addEventListener("click", async () => {
  closeMenu();

  await api("/api/logout", {
    method: "POST"
  }).catch(() => undefined);

  location.reload();
});

passwordResetButton.addEventListener("click", async () => {
  const confirmed = window.confirm(
    "Möchtest du dein Passwort wirklich zurücksetzen?\n\n" +
    "Dein bisheriges Passwort wird gelöscht und du wirst anschließend abgemeldet."
  );

  if (!confirmed) {
    return;
  }

  closeMenu();

  try {
    await api("/api/users", {
      method: "POST"
    });

    location.reload();
  } catch (error) {
    window.alert(
      error instanceof Error
        ? error.message
        : "Das Passwort konnte nicht zurückgesetzt werden."
    );
  }
});

initHideAndWatchList(filmRows);
